import { useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

/**
 * Reports the tile as seen once it scrolls into view, and gives it the GUID that ties that
 * impression to a later click on the same tile.
 *
 * The observer is shared by the whole page and reads the tile's data-offer attribute, so a
 * tile only has to register itself while it is mounted. Tiles hidden behind "Show all" or a
 * closed panel are never mounted, and so never report an impression.
 *
 * @returns {{ ref: object, guid: string }} the ref for the tile's root element and its GUID
 */
const useOfferImpression = (observer) => {
  const ref = useRef(null)
  const [guid] = useState(uuidv4)

  useEffect(() => {
    const node = ref.current
    if (!node || !observer) {
      return
    }

    observer.observe(node)

    return () => observer.unobserve(node)
  }, [observer])

  return { ref, guid }
}

/**
 * The data-offer payload the page observer turns into an offer_tile_view. Every tile builds
 * it here so the impression always carries the same fields as the click that follows it.
 */
export const offerTileData = ({
  offer,
  index,
  guid,
  isFeatured = false,
  category = null,
  categoryRowNumber = null,
}) =>
  JSON.stringify({
    id: offer.id,
    index,
    activatedOffer: Boolean(offer.activatedOffer),
    gameName: offer.gameName,
    unifiedAppId: offer.unifiedAppId,
    isUAOffer: offer.isUAOffer,
    isFeatured,
    category,
    categoryRowNumber,
    offerTileViewGUID: guid,
    campaignIdAndroid: offer.campaignIdAndroid,
    campaignIdIOS: offer.campaignIdIOS,
  })

export default useOfferImpression
